import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Dropdown, message, Pagination, Row } from "antd";
import {
  AppstoreOutlined,
  HistoryOutlined,
  EditOutlined,
  DownOutlined,
} from "@ant-design/icons";
import Button from "@components/Button";
import Table from "@components/Table";
import { getColumns } from "./config";
import { paths, permissions } from "@router";
import {
  useFetchOffersQuery,
  useReviseOfferMutation,
  useDeleteDraftOfferMutation,
} from "@services";
import { useTypedSelector } from "@hooks";
import { handleStage, resetOffers } from "@store/offers";
import { Stage } from "@types";
import { checkPermission } from "@/utils";
import CustomButton from "@/components/customButton";
import { MdOutlineFileUpload } from "react-icons/md";
import SearchBox from "@/components/searchInput";
import CustomTabs from "@/components/customTabs";
import customErrorMessage from "@/components/CustomErrorMessage";
import "./index.less";
import ActionDialog from "./ActionDialog";
import useDebounce from "@/hooks/useDebounce";
import { IoFilterSharp } from "react-icons/io5";

const Offers = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  // khurram
  const [selectedOffer, setSelectedOffer] = useState<{
    id: number;
    stage: Stage;
  } | null>(null);

  const [
    reviseOffer,
    {
      isLoading: isLoadingReviseOffer,
      isError: isErrorReviseOffer,
      isSuccess: isSuccessReviseOffer,
      error: errorReviseOffer,
    },
  ] = useReviseOfferMutation();

  const [
    deleteDraftOffer,
    {
      isLoading: isLoadingDeleteDraftOffer,
      isError: isErrorDeleteDraftOffer,
      isSuccess: isSuccessDeleteDraftOffer,
      error: errorDeleteDraftOffer,
    },
  ] = useDeleteDraftOfferMutation();
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const showModal = () => {
    setOpenEdit(true);
  };

  const handleCancel = () => {
    setOpenEdit(false);
  };

  const showDeleteDialog = () => {
    setOpenDelete(true);
  };

  const handleDeleteDialogCancel = () => {
    setOpenDelete(false);
  };
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [size, setSize] = useState("small");
  const [currentTab, setCurrentTab] = useState("1");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);


  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const { data: offersData, isFetching: isLoading } = useFetchOffersQuery({
    company_id,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    order,
    search: debouncedSearch,
    status:
      currentTab === "3"
        ? "DRAFTED"
        : currentTab === "1"
          ? "PLACED"
          : "ACCEPTED&statuses=REJECTED&statuses=EDITED",
  });
  const { data: offers, pagination } = offersData || {};

  const handleRowClick = (id: number, stage: Stage) => {
    dispatch(handleStage(stage));
    const step = Number(currentTab);
    // history.push(`/client/offers/offer/${id}`);
    // sessionStorage.setItem('state', JSON.stringify(step === 1 || step === 2 ? true : false,));
    history.push({
      pathname: `/client/offers/offer/${id}?step=${step}`,
      state: step === 1 || step === 2 ? true : false,
    });
  };

  const handleEditClick = async (id: number, stage: Stage) => {
    try {
      dispatch(handleStage(stage));
      const response = await reviseOffer({
        company_id,
        id,
        body: {},
      }).unwrap();

      message.success(`Offer Edit success`);
      history.push(`/client/offers/offer/${response?.data?.id}?mode=revise`);
      setOpenEdit(false); // close dialog after success
    } catch (err) {
      customErrorMessage("Failed to edit offer");
    }
  };
  const handleDeleteClick = async (id: number, stage: Stage) => {
    try {
      dispatch(handleStage(stage));
      const response = await deleteDraftOffer({
        company_id,
        id,
      }).unwrap();

      message.success(`Offer Deleted Successfully`);

      setOpenDelete(false); // close dialog after success
    } catch (err) {
      customErrorMessage("Failed to Delete offer");
    }
  };

  const showEditDialog = (id: number, stage: Stage) => {
    setSelectedOffer({ id, stage });
    setOpenEdit(true);
  };
  const showDeleteDraftOfferDialog = (id: number, stage: Stage) => {
    setSelectedOffer({ id, stage });
    setOpenDelete(true);
  };

  // const columns = getColumns({ handleRowClick, currentTab, handleEditClick });
  const columns = getColumns({
    handleRowClick,
    currentTab,
    handleEditClick: showEditDialog, // 👈 notice we pass showEditDialog, not handleEditClick
    handleDeleteClick: showDeleteDraftOfferDialog, // 👈 notice we pass showEditDialog, not handleEditClick
  });
  const handlePageSizeChange = (current: number, pageSize: number) => {
    // onChangePage?.(1);
    setPageSize?.(pageSize);
  };

  const [columnState, setColumnState] = useState<Record<string, string[]>>({});


  const visibleColumnKeys =
    columnState[currentTab] || columns.map(col => col.key);

  useEffect(() => {
    setColumnState(prev => {
      // if already saved for this tab → keep it
      if (prev[currentTab]) return prev;

      return {
        ...prev,
        [currentTab]: columns.map(col => col.key),
      };
    });
  }, [currentTab, columns]);


  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
               display: isDisabled ? "none" : "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                const newKeys = isChecked
                  ? visibleColumnKeys.filter(key => key !== col.key)
                  : [...visibleColumnKeys, col.key];

                setColumnState(prev => ({
                  ...prev,
                  [currentTab]: newKeys,
                }));
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );

  useEffect(() => {
    setPage(1);
    setSearchInput("")
  }, [currentTab]);


  return (
    <div className="bg-white p-4 sm:p-5">
      <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center">
        <div className="title text-white font-medium text-3xl uppercase">
          {currentTab === "3"
            ? "Drafts"
            : currentTab === "1"
              ? "Offers"
              : "Past Offers"}
        </div>
        <div className="flex flex-row justify-between">
          {currentTab === "1" && checkPermission(permissions.CREATE_OFFER) ? (
            <CustomButton
              onClick={() => {
                dispatch(resetOffers());
                history.push(paths.client.offers.create);
              }}
              btnName="Create Offer"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
              iconPosition="left"
            >
              {/* <MdOutlineFileUpload size={20} className="font-bold" /> */}
            </CustomButton>
          ) : (
            <div className="" />
          )}

          <SearchBox
            placeholder="Type to search..."
            value={searchInput}
            onChange={value => {
              setSearchInput(value);
              setPage(1);
            }}
          />
          <div className="dropdown ml-4">
            <Dropdown
              overlay={columnOverlay}
              trigger={["click"]}
              placement="bottomLeft"
            // getPopupContainer={trigger => trigger.parentElement}
            >
              <CustomButton
                btnName="Column Filter"
                customClasses="shadow-md font-medium border py-1 px-4"
              >
               <IoFilterSharp />
              </CustomButton>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="jobCreationListing mt-5 bg-white py-4 rounded-lg">


        <CustomTabs currentTab={currentTab} onTabChange={setCurrentTab}>
          <div tabKey="1">
            <Row>
              <Table
                isLoading={isLoading}
                data={offers}
                columns={filteredColumns}
                scroll={1300}
                pagination={false}
                count={pagination?.count}
                onChangePage={setPage}
                page={page}
                onPageSizeChange={setPageSize}
                onTableChange={(pagination, filters, sorter) => {
                  if (sorter?.field) {
                    setSortBy(sorter.field);
                    setOrder(sorter.order === "ascend" ? "asc" : "desc");
                    setPage(1);
                  }
                }}
              />
            </Row>
            <div className="flex justify-center border-t mt-10 pt-4">
              <Pagination
                onChange={setPage}
                current={page}
                defaultCurrent={1}
                total={pagination?.count}
                showSizeChanger
                onShowSizeChange={handlePageSizeChange}
                pageSizeOptions={["10", "20", "50", "100"]}
              />
            </div>
          </div>
          <div tabKey="2">
            <Row>
              <Table
                isLoading={isLoading}
                data={offers}
                columns={filteredColumns}
                scroll={1300}
                pagination={false}
                count={pagination?.count}
                onChangePage={setPage}
                page={page}
                onPageSizeChange={setPageSize}
                onTableChange={(pagination, filters, sorter) => {
                  if (sorter?.field) {
                    setSortBy(sorter.field);
                    setOrder(sorter.order === "ascend" ? "asc" : "desc");
                    setPage(1);
                  }
                }}
              />
            </Row>
            <div className="flex justify-center border-t mt-10 pt-4">
              <Pagination
                onChange={setPage}
                current={page}
                defaultCurrent={1}
                total={pagination?.count}
                showSizeChanger
                onShowSizeChange={handlePageSizeChange}
                pageSizeOptions={["10", "20", "50", "100"]}
              />
            </div>
          </div>
          <div tabKey="3">
            <Row>
              <Table
                isLoading={isLoading}
                data={offers}
                columns={filteredColumns}
                scroll={1300}
                pagination={false}
                count={pagination?.count}
                onChangePage={setPage}
                page={page}
                onPageSizeChange={setPageSize}
                onTableChange={(pagination, filters, sorter) => {
                  if (sorter?.field) {
                    setSortBy(sorter.field);
                    setOrder(sorter.order === "ascend" ? "asc" : "desc");
                    setPage(1);
                  }
                }}
              />
            </Row>
            <div className="flex justify-center border-t mt-10 pt-4">
              <Pagination
                onChange={setPage}
                current={page}
                defaultCurrent={1}
                total={pagination?.count}
                showSizeChanger
                onShowSizeChange={handlePageSizeChange}
                pageSizeOptions={["10", "20", "50", "100"]}
              />
            </div>
          </div>
        </CustomTabs>
      </div>
      <ActionDialog
        title="Edit Placed Offer"
        desc="Are you sure you want to edit this offer? A new version will be
                    created, and the current offer will be archived under the Past Offers
                    section."
        visible={openEdit}
        handleCancel={handleCancel}
        handleOk={() => {
          if (selectedOffer) {
            handleEditClick(selectedOffer.id, selectedOffer.stage);
          }
        }}
        loading={isLoadingReviseOffer}
        action="Edit"
      />
      <ActionDialog
        title="Delete Draft Offer"
        desc="Are you sure you want to Delete this offer?"
        visible={openDelete}
        handleCancel={handleDeleteDialogCancel}
        handleOk={() => {
          if (selectedOffer) {
            handleDeleteClick(selectedOffer.id, selectedOffer.stage);
          }
        }}
        loading={isLoadingDeleteDraftOffer}
        action="Delete"
      />
    </div>
  );
};

export default Offers;
// import { useHistory } from "react-router-dom";
// import { useEffect, useState } from "react";
// import { useDispatch } from "react-redux";
// import { Dropdown, message, Pagination, Row } from "antd";
// import {
//   AppstoreOutlined,
//   HistoryOutlined,
//   EditOutlined,
//   DownOutlined,
// } from "@ant-design/icons";
// import Button from "@components/Button";
// import Table from "@components/Table";
// import { getColumns } from "./config";
// import { paths, permissions } from "@router";
// import {
//   useFetchOffersQuery,
//   useReviseOfferMutation,
//   useDeleteDraftOfferMutation,
// } from "@services";
// import { useTypedSelector } from "@hooks";
// import { handleStage, resetOffers } from "@store/offers";
// import { Stage } from "@types";
// import { checkPermission } from "@/utils";
// import CustomButton from "@/components/customButton";
// import { MdOutlineFileUpload } from "react-icons/md";
// import SearchBox from "@/components/searchInput";
// import CustomTabs from "@/components/customTabs";
// import customErrorMessage from "@/components/CustomErrorMessage";
// import "./index.less";
// import ActionDialog from "./ActionDialog";

// const Offers = () => {
//   const history = useHistory();
//   const dispatch = useDispatch();
//   // khurram
//   const [selectedOffer, setSelectedOffer] = useState<{
//     id: number;
//     stage: Stage;
//   } | null>(null);

//   const [
//     reviseOffer,
//     {
//       isLoading: isLoadingReviseOffer,
//       isError: isErrorReviseOffer,
//       isSuccess: isSuccessReviseOffer,
//       error: errorReviseOffer,
//     },
//   ] = useReviseOfferMutation();

//   const [
//     deleteDraftOffer,
//     {
//       isLoading: isLoadingDeleteDraftOffer,
//       isError: isErrorDeleteDraftOffer,
//       isSuccess: isSuccessDeleteDraftOffer,
//       error: errorDeleteDraftOffer,
//     },
//   ] = useDeleteDraftOfferMutation();
//   const [openEdit, setOpenEdit] = useState(false);
//   const [openDelete, setOpenDelete] = useState(false);
//   const [confirmLoading, setConfirmLoading] = useState(false);
//   const [pageSize, setPageSize] = useState(10);

//   const showModal = () => {
//     setOpenEdit(true);
//   };

//   const handleCancel = () => {
//     setOpenEdit(false);
//   };

//   const showDeleteDialog = () => {
//     setOpenDelete(true);
//   };

//   const handleDeleteDialogCancel = () => {
//     setOpenDelete(false);
//   };
//   const [page, setPage] = useState(1);
//   const [searchInput, setSearchInput] = useState("");
//   const [size, setSize] = useState("small");
//   const [currentTab, setCurrentTab] = useState("1");
//   const [sortBy, setSortBy] = useState<string | null>(null);
//   const [order, setOrder] = useState<"asc" | "desc" | null>(null);


//   const { company } = useTypedSelector(state => state.auth?.user!);
//   const { id: company_id } = company!;
//   const { data: offersData, isFetching: isLoading } = useFetchOffersQuery({
//     company_id,
//     page,
//     page_size: pageSize,
//     sort_by: sortBy,
//     order: order,
//     status:
//       currentTab === "3"
//         ? "DRAFTED"
//         : currentTab === "1"
//           ? "PLACED"
//           : "ACCEPTED&statuses=REJECTED&statuses=EDITED",
//   });
//   const { data: offers, pagination } = offersData || {};

//   const handleRowClick = (id: number, stage: Stage) => {
//     dispatch(handleStage(stage));
//     const step = Number(currentTab);
//     // history.push(`/client/offers/offer/${id}`);
//     // sessionStorage.setItem('state', JSON.stringify(step === 1 || step === 2 ? true : false,));
//     history.push({
//       pathname: `/client/offers/offer/${id}?step=${step}`,
//       state: step === 1 || step === 2 ? true : false,
//     });
//   };

//   const handleEditClick = async (id: number, stage: Stage) => {
//     try {
//       dispatch(handleStage(stage));
//       const response = await reviseOffer({
//         company_id,
//         id,
//         body: {},
//       }).unwrap();

//       message.success(`Offer Edit success`);
//       history.push(`/client/offers/offer/${response?.data?.id}?mode=revise`);
//       setOpenEdit(false); // close dialog after success
//     } catch (err) {
//       customErrorMessage("Failed to edit offer");
//     }
//   };
//   const handleDeleteClick = async (id: number, stage: Stage) => {
//     try {
//       dispatch(handleStage(stage));
//       const response = await deleteDraftOffer({
//         company_id,
//         id,
//       }).unwrap();

//       message.success(`Offer Deleted Successfully`);

//       setOpenDelete(false); // close dialog after success
//     } catch (err) {
//       customErrorMessage("Failed to Delete offer");
//     }
//   };

//   const showEditDialog = (id: number, stage: Stage) => {
//     setSelectedOffer({ id, stage });
//     setOpenEdit(true);
//   };
//   const showDeleteDraftOfferDialog = (id: number, stage: Stage) => {
//     setSelectedOffer({ id, stage });
//     setOpenDelete(true);
//   };

//   // const columns = getColumns({ handleRowClick, currentTab, handleEditClick });
//   const columns = getColumns({
//     handleRowClick,
//     currentTab,
//     handleEditClick: showEditDialog, // 👈 notice we pass showEditDialog, not handleEditClick
//     handleDeleteClick: showDeleteDraftOfferDialog, // 👈 notice we pass showEditDialog, not handleEditClick
//   });
//   const handlePageSizeChange = (current: number, pageSize: number) => {
//     // onChangePage?.(1);
//     setPageSize?.(pageSize);
//   };

//   const [visibleColumnKeys, setVisibleColumnKeys] = useState(
//     columns.map(col => col.key)
//   );



//   useEffect(() => {
//     setVisibleColumnKeys(columns.map(col => col.key));
//   }, [currentTab]);

//   // ✅ FILTERED COLUMNS
//   const filteredColumns = columns.filter(col =>
//     visibleColumnKeys.includes(col.key)
//   );

//   // custom overlay
//   const columnOverlay = (
//     <div
//       style={{
//         background: "#fff",
//         padding: 8,
//         borderRadius: 6,
//         boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
//         minWidth: 240,
//         maxHeight: 300,
//         overflowY: "auto",
//       }}
//       onClick={(e) => e.stopPropagation()}
//     >
//       {columns.map((col) => {
//         const isActionsColumn = col.key === "actions";
//         const isChecked = visibleColumnKeys.includes(col.key);
//         const isDisabled =
//           isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

//         return (
//           <label
//             key={col.key}
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 12,
//               padding: "6px 8px",
//               borderRadius: 4,
//               cursor: isDisabled ? "not-allowed" : "pointer",
//               opacity: isDisabled ? 0.6 : 1,
//             }}
//           >
//             <input
//               type="checkbox"
//               checked={isChecked}
//               disabled={isDisabled}
//               onChange={() => {
//                 if (isActionsColumn) return;

//                 if (isChecked) {
//                   setVisibleColumnKeys(
//                     visibleColumnKeys.filter((key) => key !== col.key)
//                   );
//                 } else {
//                   setVisibleColumnKeys([...visibleColumnKeys, col.key]);
//                 }
//               }}
//               style={{
//                 margin: 0,
//                 width: 16,
//                 height: 16,
//                 flexShrink: 0,
//               }}
//             />

//             <span
//               style={{
//                 flex: 1,
//                 fontSize: 14,
//                 lineHeight: "18px",
//                 whiteSpace: "nowrap",
//                 overflow: "hidden",
//                 textOverflow: "ellipsis",
//               }}
//             >
//               {col.title}
//             </span>
//           </label>
//         );
//       })}
//     </div>
//   );


//   return (
//     <div>
//       <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center">
//         <div className="title text-themeBlue font-medium text-3xl">
//           {currentTab === "3"
//             ? "Drafts"
//             : currentTab === "1"
//               ? "Offers"
//               : "Past Offers"}
//         </div>
//         <div className="flex flex-row justify-between">
//           {currentTab === "1" && checkPermission(permissions.CREATE_OFFER) ? (
//             <CustomButton
//               onClick={() => {
//                 dispatch(resetOffers());
//                 history.push(paths.client.offers.create);
//               }}
//               btnName="Create Offer"
//               customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
//               iconPosition="left"
//             >
//               <MdOutlineFileUpload size={20} className="text-white font-bold" />
//             </CustomButton>
//           ) : (
//             <div className="mb-20" />
//           )}
//         </div>
//       </div>
//       <div className="jobCreationListing   mt-6 bg-white px-5 py-4 rounded-lg">
//         <div className="flex items-center justify-end gap-2">
//           <SearchBox
//             placeholder="Type to search..."
//             value={searchInput}
//             onChange={value => setSearchInput(value)}
//           />
//           <Dropdown
//             overlay={columnOverlay}
//             trigger={["click"]}
//             placement="bottomLeft"
//           // getPopupContainer={trigger => trigger.parentElement}
//           >
//             <CustomButton
//               btnName="Columns"
//               customClasses="shadow-md font-medium border py-1 px-4"
//             >
//              <IoFilterSharp />
//             </CustomButton>
//           </Dropdown>
//         </div>

//         <CustomTabs currentTab={currentTab} onTabChange={setCurrentTab}>
//           <div tabKey="1">
//             <Row>
//               <Table
//                 isLoading={isLoading}
//                 data={offers}
//                 columns={filteredColumns}
//                 scroll={1300}
//                 pagination={false}
//                 count={pagination?.count}
//                 onChangePage={setPage}
//                 page={page}
//                 onPageSizeChange={setPageSize}
//                 onTableChange={(pagination, filters, sorter) => {
//                   if (sorter?.field) {
//                     setSortBy(sorter.field);
//                     setOrder(sorter.order === "ascend" ? "asc" : "desc");
//                     setPage(1);
//                   }
//                 }}
//               />
//             </Row>
//             <div className="flex justify-center border-t mt-10 pt-4">
//               <Pagination
//                 onChange={setPage}
//                 current={page}
//                 defaultCurrent={1}
//                 total={pagination?.count}
//                 showSizeChanger
//                 onShowSizeChange={handlePageSizeChange}
//                 pageSizeOptions={["10", "20", "50", "100"]}
//               />
//             </div>
//           </div>
//           <div tabKey="2">
//             <Row>
//               <Table
//                 isLoading={isLoading}
//                 data={offers}
//                 columns={filteredColumns}
//                 scroll={1300}
//                 pagination={false}
//                 count={pagination?.count}
//                 onChangePage={setPage}
//                 page={page}
//                 onPageSizeChange={setPageSize}
//                 onTableChange={(pagination, filters, sorter) => {
//                   if (sorter?.field) {
//                     setSortBy(sorter.field);
//                     setOrder(sorter.order === "ascend" ? "asc" : "desc");
//                     setPage(1);
//                   }
//                 }}
//               />
//             </Row>
//             <div className="flex justify-center border-t mt-10 pt-4">
//               <Pagination
//                 onChange={setPage}
//                 current={page}
//                 defaultCurrent={1}
//                 total={pagination?.count}
//                 showSizeChanger
//                 onShowSizeChange={handlePageSizeChange}
//                 pageSizeOptions={["10", "20", "50", "100"]}
//               />
//             </div>
//           </div>
//           <div tabKey="3">
//             <Row>
//               <Table
//                 isLoading={isLoading}
//                 data={offers}
//                 columns={filteredColumns}
//                 scroll={1300}
//                 pagination={false}
//                 count={pagination?.count}
//                 onChangePage={setPage}
//                 page={page}
//                 onPageSizeChange={setPageSize}
//                 onTableChange={(pagination, filters, sorter) => {
//                   if (sorter?.field) {
//                     setSortBy(sorter.field);
//                     setOrder(sorter.order === "ascend" ? "asc" : "desc");
//                     setPage(1);
//                   }
//                 }}
//               />
//             </Row>
//             <div className="flex justify-center border-t mt-10 pt-4">
//               <Pagination
//                 onChange={setPage}
//                 current={page}
//                 defaultCurrent={1}
//                 total={pagination?.count}
//                 showSizeChanger
//                 onShowSizeChange={handlePageSizeChange}
//                 pageSizeOptions={["10", "20", "50", "100"]}
//               />
//             </div>
//           </div>
//         </CustomTabs>
//       </div>
//       <ActionDialog
//         title="Edit Placed Offer"
//         desc="Are you sure you want to edit this offer? A new version will be
//                     created, and the current offer will be archived under the Past Offers
//                     section."
//         visible={openEdit}
//         handleCancel={handleCancel}
//         handleOk={() => {
//           if (selectedOffer) {
//             handleEditClick(selectedOffer.id, selectedOffer.stage);
//           }
//         }}
//         loading={isLoadingReviseOffer}
//         action="Edit"
//       />
//       <ActionDialog
//         title="Delete Draft Offer"
//         desc="Are you sure you want to Delete this offer?"
//         visible={openDelete}
//         handleCancel={handleDeleteDialogCancel}
//         handleOk={() => {
//           if (selectedOffer) {
//             handleDeleteClick(selectedOffer.id, selectedOffer.stage);
//           }
//         }}
//         loading={isLoadingDeleteDraftOffer}
//         action="Delete"
//       />
//     </div>
//   );
// };

// export default Offers;
